import React, { createContext, useState, useContext, ReactNode } from 'react';
import AuthService from '../services/AuthService';
import { useNavigate } from 'react-router-dom';
import { useNotification } from "../contexts/NotificationContext";
import  config  from '../contexts/ConfigContext'
import { t } from 'i18next';
import { UserService } from '../services/UserService';

interface AuthContextType {
    isAuthenticated: boolean;
    login: (username: string, password: string, redirectToHome: boolean) => Promise<any>;
    logout: () => void;
    validateToken: (redirectToHome: boolean) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const notifications = useNotification();
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
    const navigate = useNavigate();
    const userService = new UserService();

    const login = async (username: string, password: string, redirectToHome: boolean) : Promise<any> => {
        try {
            let authService = new AuthService();
            const response = await authService.login(username, password);
            const res = await response;
            if (res.token) {
                const respPermissions = await authService.getPermissions(config.PERMISSION_CODE);
                switch (await respPermissions.status){
                    case 200:
                        setIsAuthenticated(true);
                        if (redirectToHome)
                            window.location.href = `${process.env.REACT_APP_DEPLOY_PATH}/dashboard`;
                           // navigate("/dashboard");
                           return true;
                    case 403:
                        notifications.showNotification(t('general.unaouthorizedMessage'), 'error');
                        ClearLocalStorage();
                        break;
                    default:
                        console.log(respPermissions);
                        notifications.showNotification("Error getting permissions", 'error');
                        break;                   
                }
            }
            else {
                setIsAuthenticated(false);
                notifications.showNotification(
                    "Invalid username or password",
                    "warning"
                  );
            }
            return;
        } catch (error: any) {
            console.error(error);
            if ((error as any).response && (error as any).response.status === 401) {
                notifications.showNotification(
                  "Invalid username or password",
                  "warning"
                );
              } 
              else {
            notifications.showNotification("Our system encountered an unexpected error (500). We're working on fixing it as soon as possible. Please try again soon, or contact support if you need immediate help.", 'error');
            }
        }
    };


    const logout = () => {
        ClearLocalStorage();
        navigate("/login");
    };
    const ClearLocalStorage = () => {
        localStorage.removeItem('sso-auth');
        localStorage.removeItem('sso-auth-expiration-date');
        localStorage.removeItem('sso-auth-userId');
        localStorage.removeItem('sso-auth-user');
        sessionStorage.removeItem('startDate');
        sessionStorage.removeItem('endDate');
        setIsAuthenticated(false);
    }

    const validateToken = async (redirectToHome: boolean) => {

        let authService = new AuthService();
        const response = await authService.validate();
        const res = await response;
        if (res) {
            const respPermissions = await authService.getPermissions(config.PERMISSION_CODE);
            switch (await respPermissions.status){
                case 200:
                    setIsAuthenticated(true);
                    await userService.GetUser();
                    if (redirectToHome)
                    navigate("/dashboard");
                    break;
                case 401:
                    notifications.showNotification("User has no permissions", 'error');
                    break;
                default:
                    console.log(respPermissions);
                    notifications.showNotification("Error getting permissions", 'error');
                    break;                   
            }
        }
        else {
            setIsAuthenticated(false);
            navigate("/login");
        }
        return;
    }

    return (
        <AuthContext.Provider value={{ isAuthenticated, login, logout, validateToken }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (context) {
        let token = localStorage.getItem('sso-auth');
        let expirationDate = localStorage.getItem('sso-auth-expiration-date');
        if (token && expirationDate) {
            let expirationDateObj = new Date(expirationDate);
            let currentDate = new Date();
            if (expirationDateObj < currentDate) {
                context.isAuthenticated = false;
                localStorage.removeItem('sso-auth');
                localStorage.removeItem('sso-auth-expiration-date');
                localStorage.removeItem('sso-auth-userId');
                localStorage.removeItem('sso-auth-user');
            }
            else {

                context.isAuthenticated = true;
            }
        }
        else
            context.isAuthenticated = false;
    }
    else {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};